import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV, waLink } from "@/data/site";
import { track } from "@/lib/api";
import { MagneticButton } from "@/components/pp/Shared";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    NAV.forEach((n) => { const el = document.getElementById(n.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    document.body.classList.toggle("pp-no-scroll", open);
  }, [open]);

  const go = (id) => { setOpen(false); scrollTo(id); };

  return (
    <>
      <motion.header
        initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-[80] transition-colors duration-300 ${scrolled ? "pp-glass" : "bg-transparent"}`}
        data-testid="navbar"
      >
        <nav className="pp-container flex items-center justify-between h-[70px]">
          <button onClick={() => go("home")} className="flex items-center gap-2 shrink-0" data-testid="nav-logo" aria-label="PixelPapad home">
            <img src="/brand/logo-dark.png" alt="PixelPapad" className="h-8 md:h-9 object-contain" />
          </button>

          <div className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                data-testid={`nav-${n.id}`}
                className="relative px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                {n.label}
                {active === n.id && (
                  <motion.span layoutId="nav-active" className="absolute left-3 right-3 -bottom-0.5 h-[2px] bg-[#f59e0b]" />
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href={waLink()} target="_blank" rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { location: "navbar" })}
              data-testid="nav-whatsapp"
              className="grid place-items-center h-10 w-10 rounded-full border border-white/10 text-[#25D366] hover:bg-white/5 transition-colors"
              aria-label="Chat on WhatsApp"
            >
              <MessageCircle size={18} />
            </a>
            <MagneticButton
              onClick={() => { track("cta_click", { cta: "start_project", location: "navbar" }); go("planner"); }}
              data-testid="nav-cta"
              className="hidden sm:inline-flex items-center bg-[#f59e0b] text-black font-semibold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white transition-colors"
            >
              Start Your Project
            </MagneticButton>
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden grid place-items-center h-10 w-10 text-white"
              data-testid="nav-menu-open" aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-[#030305]/98 backdrop-blur-xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="pp-container flex items-center justify-between h-[70px]">
              <img src="/brand/logo-dark.png" alt="PixelPapad" className="h-8" />
              <button onClick={() => setOpen(false)} className="h-10 w-10 grid place-items-center text-white" data-testid="nav-menu-close" aria-label="Close menu">
                <X size={26} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center pp-container gap-1 overflow-y-auto">
              {NAV.map((n, i) => (
                <motion.button
                  key={n.id}
                  initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04 }}
                  onClick={() => go(n.id)}
                  data-testid={`mobile-nav-${n.id}`}
                  className="text-left font-display uppercase text-4xl sm:text-5xl py-2 text-white/90 hover:text-[#f59e0b] transition-colors"
                >
                  {n.label}
                </motion.button>
              ))}
            </div>
            <div className="pp-container pb-10">
              <button
                onClick={() => go("planner")}
                className="w-full bg-[#f59e0b] text-black font-semibold uppercase tracking-widest text-sm py-4"
                data-testid="mobile-nav-cta"
              >
                Start Your Project
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export { scrollTo };
